<template>
  <div>
    <breadcrumbs :home="vessel.name + ': Reduce inventory'"/>

      <v-card flat>
        <v-form v-model="valid" ref="form" :disabled="!edit">
          <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

            <v-row dense>
              <v-col cols="6" sm="4" lg="3">
                <date-picker v-model="transaction.date" @change="refresh(false)"/>
              </v-col>
              <v-col class="text-caption ml-5 mt-3 grey--text">{{vesselContents}}</v-col>
            </v-row>

            <v-row dense>
              <v-col cols="6" sm="3" lg="2">
                <alcohol-input label="Reduce by"
                               :spirit="spirit || !this.vessel.contents.volume"
                               :tare="vessel.tare" v-model="transaction.volume" :litres.sync="transaction.litres"
                               :kg.sync="transaction.kg" :strength.sync="transaction.strength"
                               :disabled="!edit" :hint="vesselVolume"/>
              </v-col>
              <v-col cols="6" sm="3" lg="2">
                <decimal-number-input label="Total value to reduce" v-model="value" :disabled="calculateValue || !edit"/>
              </v-col>
              <v-col cols="6" sm="3" lg="2">
                <v-checkbox label="Calculate value" v-model="calculateValue" dense class="mt-1"/>
              </v-col>
              <v-col>
                <v-btn x-small text class="mt-3" @click="allContents()">
                  Use all contents
                </v-btn>
              </v-col>
            </v-row>

            <v-row dense v-if="!vessel.contents.userLicense && vessel.contents.alcoholType && vessel.contents.alcoholType !== 'NonExcise'">
              <v-col>
                <v-radio-group v-model="dutyPaid" class="mt-0">
                  <v-radio :value="false">
                    <template v-slot:label>
                      Non-duty-paid
                      <span class="text-caption ml-5" v-if="!$vuetify.breakpoint.xs">(e.g. taken for analysis or destroyed in an approved manner)</span>
                    </template>
                  </v-radio>
                  <v-radio :value="true">
                    <template v-slot:label>
                      Duty-paid
                      <span class="text-caption ml-5" v-if="!$vuetify.breakpoint.xs">(e.g. taken for use or unaccounted losses)</span>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-col>
            </v-row>

            <v-row v-if="values && company.connectedToAccounting">
              <v-col cols="12" sm="6" lg="4">
                <account-picker v-model="transaction.expenseAccountRef" label="Inventory adjustment account"
                                :accounts="accounts"
                                hint="An account to balance the inventory asset account. Usually an inventory shrinkage expense or cost of goods account."/>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field label="Comments" v-model="transaction.comments" outlined dense counter="255" :rules="[fieldRules.required,fieldRules.maxLength(255)]"/>
              </v-col>
            </v-row>
          </v-container>
        </v-form>

        <v-divider/>
        <v-card-actions class="px-6">
          <template v-if="transactionId && company.permissions.includes('production.write')">
            <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                      v-model="edit" label="Edit" hide-details class="mt-0"/>
            <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
          </template>
          <v-spacer/>
          <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
          <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'

export default {
  name: 'ExciseVesselReduceInventory',
  mixins: [confirmLeave],
	props: {
    vesselId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      edit: false,
      vessel: {name:'',contents:{userLicense:true}},
      valid:true,
      saving:false,
      accounts: [],
      calculateValue: true,
      value: 0,
      dutyPaid: false,
      transaction: {vesselId:this.vesselId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), excise:null,
                    volume: 0, litres: 0, kg:0, strength: 0, value:null, comments: 'Production loss',expenseAccountRef:''},
      dirty: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.$store.getters.company.settings.useAccounts;
    },
    spirit() {
      return this.vessel.contents?.alcoholType === 'Spirit';
    },
    vesselContents() {
      return this.vessel.contents.volume ? this.vessel.contents.alcoholName : '';
    },
    vesselVolume() {
      if (!this.transactionId)
        return this.vessel.contents.volume ? `${this.$number(this.vessel.contents.volume, 3)} ${this.spirit?'LAA':'litres'}` : '(empty)';
    }
  },
  watch: {
    calculateValue() {
      if (this.calculateValue)
        this.transaction.value = null;
      this.dirty = true;
    },
    value() {
      this.dirty = true;
    },
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    const results = await this.$apiCall(`query ExciseVesselReduceInventory($currency:String!,$id:ID!,$edit:Boolean!) {
                                              accounts(currency:$currency) {
                                                ref,parentRef,name,type,typeName
                                              }
                                              exciseVesselReduceInventory(id:$id) @include(if:$edit) {
                                                id,vesselId,date,volume,litres,kg,strength,value,excise,comments,expenseAccountRef
                                              }
                                              companyAccounts @skip(if:$edit) {inventoryAdjustment}
                                            }`,
                                        {currency:this.company.settings.homeCurrency,
                                         id:this.transactionId||'',edit:this.transactionId!=undefined});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    this.accounts = results.data.accounts;
    if (this.transactionId) {
      this.transaction = results.data.exciseVesselReduceInventory;
      this.dutyPaid = this.transaction.excise === 'WineReductionDutyPaid' || this.transaction.excise === 'SpiritsReductionOtherDutyPaid';
      this.calculateValue = this.transaction.value == null;
      this.value = this.transaction.value || 0;
    }
    if (!this.transactionId) {
      this.transaction.expenseAccountRef = results.data.companyAccounts.inventoryAdjustment;
      this.edit = true;
    }
    await this.refresh(true);
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    allContents() {
      this.transaction.volume = this.vessel.contents.volume;
      this.transaction.litres = this.vessel.contents.litres;
      this.transaction.kg = this.vessel.contents.kg;
      this.transaction.strength = this.vessel.contents.strength;
    },
    async refresh(firstTime) {
      const results = await this.$apiCall(`query ExciseVesselReduceInventory($id:ID!, $date:DateTime) {
                                                exciseVessel(id:$id) {
                                                  id,name,tare,
                                                  contents(date:$date) {alcoholType,alcoholName,userLicense,volume,litres,strength}
                                                }
                                              }`,
                                          {id:this.transaction.vesselId || this.vesselId, date:this.transaction.date});
      if (!results.error) {
        this.vessel = results.data.exciseVessel;
        if (!firstTime || !this.transactionId)
          this.transaction.strength = this.vessel.contents.strength;
        this.$nextTick(() => {this.$refs.form.validate()});
      }
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      if (this.valid) {
        this.saving = true;
        this.transaction.value = this.calculateValue ? null : this.value;
        if (this.vessel.contents.userLicense)
          this.transaction.excise = this.vessel.contents.alcoholType === 'Spirit' ? 'UserSpiritsReductionOtherPurpose' : 'UserWineReductionOther';
        else if (this.vessel.contents.alcoholType === 'Spirit')
          this.transaction.excise = this.dutyPaid ? 'SpiritsReductionOtherDutyPaid' : 'SpiritsReductionOtherNonDutyPaid';
        else if (this.vessel.contents.alcoholType.startsWith('Wine'))
          this.transaction.excise = this.dutyPaid ? 'WineReductionDutyPaid' : 'WineReductionNonDutyPaid';
        else
          this.transaction.excise = null;
        const results = await this.$apiCall(`mutation ExciseVesselReduceInventory($input: ExciseVesselReduceInventoryInput!) {
                                                exciseVesselReduceInventoryCreate(input:$input) {id}
                                              }`,
                                            {input:this.transaction});
        this.saving = false;
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} reduce inventory transaction for ${this.vessel.name}`});
        }
      }
		}
	}
}
</script>